import { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  Menu,
  MenuItem,
  Divider,
} from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import PanToolIcon from "@mui/icons-material/PanTool";
import AspectRatioIcon from "@mui/icons-material/AspectRatio";
import FitScreenIcon from "@mui/icons-material/FitScreen";
import CropFreeIcon from "@mui/icons-material/CropFree";
import RefreshIcon from "@mui/icons-material/Refresh";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import InfoIcon from "@mui/icons-material/Info";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PrintIcon from "@mui/icons-material/Print";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import CloseIcon from "@mui/icons-material/Close";
import Timer from "./Timer";
import ImageContextMenu from "./ImageContextMenu";
import PinContextMenu from "./PinContextMenu";
import Pin from "./Pin";
import { ViewerInfoDrawer } from "../ViewerInfoDrawer";
import FullscreenButton from "./FullscreenButton";
import { useToast } from "../../../contexts/ToastContext";
import FullscreenRecodingTab from "./FullscreenRecodingTab";
import { PinEditDialog } from "../PinEditDialog";
import { get, post, put } from "../../../services/api.js";

function PinImageContainer({
  imageUrl,
  pins1,
  createPin,
  viewedSession,
  currentSession,
  updatePin,
  deletePin,
  playMp4Recording,
  recording,
  getPinsBySessionId,
  selectedDate,
  renderCamera360,
  streamType,
  mp4Url,
  recordingsTabFullscreenOpen,
  onCloseFullscreen,
  streamStatus,
  fileData = null,
  sessionList = [],
  selectedDrawingForView,
  showLabel,
  
}) {

  console.log('viewedSession in pin image container',viewedSession)
  
  const { showSuccess, showError, showWarning } = useToast();

  const [contextMenu, setContextMenu] = useState(null);
  const [pinContextMenu, setPinContextMenu] = useState(null);
  const [pinEditDialog, setPinEditDialog] = useState(null);
  const [pins, setPins] = useState(pins1);
  const [draggedPin, setDraggedPin] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [mouseDownPos, setMouseDownPos] = useState({ x: 0, y: 0 });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [editingPinId, setEditingPinId] = useState(null);
  const [activeTimers, setActiveTimers] = useState([]);
  const justDraggedRef = useRef(false);
  const containerRef = useRef(null); // For fullscreen - attached to outermost container
  const imageContainerRef = useRef(null); // For coordinate calculations - attached to image container
  
  // Use a fixed key for tab-level persistence (not per image)
  const TAB_STATE_KEY = 'pinImageContainer_tabState';
  
  // Track current image to detect when it changes
  const currentImageIdRef = useRef(null);
  
  // Get current image identifier
  const getCurrentImageId = useCallback(() => {
    return fileData?.id || fileData?.name || imageUrl || null;
  }, [fileData?.id, fileData?.name, imageUrl]);

  // Load persisted state from localStorage (only if same image)
  const loadPersistedState = useCallback(() => {
    const currentImageId = getCurrentImageId();
    if (!currentImageId) {
      return {
        zoom: 100,
        panOffset: { x: 0, y: 0 },
        rotation: 0,
      };
    }
    
    // Try to load persisted state
    try {
      const savedState = localStorage.getItem(TAB_STATE_KEY);
      if (savedState) {
        const parsed = JSON.parse(savedState);
        // Only restore if it's for the same image
        if (parsed.imageId === currentImageId) {
          return {
            zoom: parsed.zoom ?? 100,
            panOffset: parsed.panOffset ?? { x: 0, y: 0 },
            rotation: parsed.rotation ?? 0,
          };
        }
      }
    } catch (error) {
      console.warn('Failed to load persisted zoom/pan state:', error);
    }
    
    // Default state for new image
    return {
      zoom: 100,
      panOffset: { x: 0, y: 0 },
      rotation: 0,
    };
  }, [getCurrentImageId]);

  // Initialize state
  const currentImageId = getCurrentImageId();
  const initialState = loadPersistedState();
  const [zoom, setZoom] = useState(initialState.zoom);
  const [isPanMode, setIsPanMode] = useState(false);
  const [panOffset, setPanOffset] = useState(initialState.panOffset);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(initialState.rotation);
  
  // Update current image ID ref
  currentImageIdRef.current = currentImageId;
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [showInfo, setShowInfo] = useState(false);
  const[selectedPinId, setSelectedPinId] = useState(null);
  const [activities, setActivities] = useState([]);
  const [isLoadingActivities, setIsLoadingActivities] = useState(false);

  // Helper function to get image dimensions
  const getImageDimensions = useCallback(() => {
    if (imageContainerRef.current) {
      const rect = imageContainerRef.current.getBoundingClientRect();
      return { width: rect.width, height: rect.height };
    }
    // Fallback dimensions
    return { width: 1000, height: 800 };
  }, []);

  // Helper function to convert mouse coordinates to image coordinates accounting for zoom, pan, and rotation
  const getImageCoordinates = useCallback(
    (mouseX, mouseY) => {
      const container = imageContainerRef.current;
      if (!container) return { x: 0, y: 0 };

      const rect = container.getBoundingClientRect();
      const { width: imageWidth, height: imageHeight } = getImageDimensions();
      
      // Get container center (transform origin)
      const containerCenterX = rect.width / 2;
      const containerCenterY = rect.height / 2;
      
      // Get mouse position relative to container
      const containerX = mouseX - rect.left;
      const containerY = mouseY - rect.top;
      
      // Convert to coordinates relative to center (transform origin)
      // This is the point after all transformations
      let x = containerX - containerCenterX;
      let y = containerY - containerCenterY;
      
      // Reverse the CSS transform chain: scale(zoom) translate(pan) rotate(angle)
      // CSS applies transforms right-to-left: rotate → translate → scale
      // To reverse, we apply: reverse-scale → reverse-translate → reverse-rotate
      
      // Step 1: Reverse scale (divide by zoom factor to get coordinates in scaled space)
      const zoomFactor = zoom / 100;
      x = x / zoomFactor;
      y = y / zoomFactor;
      
      // Step 2: Reverse translation
      // The panOffset in the transform is divided by zoom: panOffset.x / zoomFactor
      // So we subtract the same value
      x = x - (panOffset.x / zoomFactor);
      y = y - (panOffset.y / zoomFactor);
      
      // Step 3: Reverse rotation (rotate by -rotation degrees around center)
      if (rotation !== 0) {
        const angleRad = (-rotation * Math.PI) / 180;
        const cos = Math.cos(angleRad);
        const sin = Math.sin(angleRad);
        // Rotate point (x, y) by -angle around origin
        const rotatedX = x * cos - y * sin;
        const rotatedY = x * sin + y * cos;
        x = rotatedX;
        y = rotatedY;
      }
      
      // Convert from center-relative coordinates to absolute image coordinates
      const finalX = x + imageWidth / 2;
      const finalY = y + imageHeight / 2;
      
      return { x: finalX, y: finalY };
    },
    [zoom, panOffset, rotation, getImageDimensions]
  );
  const [isFullscreenSupported, setIsFullscreenSupported] = useState(false);

  // Check if we're viewing a previous session (not current session)
  // If currentSession is null, we're viewing a historical date (previous session)
  // If currentSession exists, compare IDs to determine if it's a previous session
  const isViewingPreviousSession = viewedSession && (
    !currentSession || 
    (currentSession.id !== viewedSession.id)
  );

  const handleContextMenu = (event) => {
    if(isPanMode){
      setIsPanMode(false);
    }
    event.preventDefault();

    // Prevent opening context menu if any timer is running
    if (activeTimers.length > 0) {
      return;
    }

    const container = imageContainerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    // Get image coordinates accounting for zoom and pan
    const imageCoords = getImageCoordinates(event.clientX, event.clientY);

    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX,
            mouseY: event.clientY,
            relativeX: x,
            relativeY: y,
            imageX: imageCoords.x,
            imageY: imageCoords.y,
            openedFromPin: false, // Context menu opened from image, not pin
            containerRect: {
              left: rect.left,
              top: rect.top,
              width: rect.width,
              height: rect.height,
            },
          }
        : null
    );
  };

  const handleAddPin = (duration = "15",createPinData) => {
    
  
    // const sessionToUse = contextMenu?.openedFromPin && isViewingPreviousSession && currentSession
    // ? currentSession
    // : viewedSession;

    const sessionToUse =currentSession
   

  if(!sessionToUse||Object.keys(sessionToUse)?.length===0){
    showError("Please create a session first for current date or select previous date for create pin. ");
    return null;
  }
    // Check if sessionList is empty and selectedDate is less than today
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDateNormalized = selectedDate ? new Date(selectedDate) : null;
    if (selectedDateNormalized) {
      selectedDateNormalized.setHours(0, 0, 0, 0);
    }
    
    if (sessionList.length === 0 && selectedDateNormalized && selectedDateNormalized < today) {
      showError("Cannot add pin");
      return;
    }
    
    // Determine which session to use for creating the pin
    // If context menu was opened from a previous session pin, use currentSession
    // Otherwise, use viewedSession (for image right-clicks)
   
    if(streamStatus !== "streaming"){
      showError("Please start the stream first");
      return;
    }
    let isStreamStart =
      localStorage.getItem("streamStatus") === "streaming" ? true : false;
    if (!isStreamStart) {
      showError("Please start the stream first");
      return;
    }

    if (contextMenu) {
      // Use image coordinates to calculate percentages
      const { width: imageWidth, height: imageHeight } = getImageDimensions();
      const xPercent = contextMenu.imageX / imageWidth;
      const yPercent = contextMenu.imageY / imageHeight;

      const durationSeconds = parseInt(duration) || 15;

      // Create timer
      const newTimer = {
        id: Date.now(),
        xPercent: xPercent,
        yPercent: yPercent,
        duration: durationSeconds.toString(),
        startTime: Date.now(),
      };

      setActiveTimers((prev) => [...prev, newTimer]);
      createPin(sessionToUse,createPinData?createPinData:{
        // id: Date.now(),
        xPercent: xPercent,
        yPercent: yPercent,
        duration: durationSeconds.toString(),
        // createdAt: Date.now(),
      });

      // Create pin after timer completes
      setTimeout(() => {
        const newPin = {
          id: Date.now(),
          xPercent: xPercent,
          yPercent: yPercent,
          duration: durationSeconds.toString(),
          createdAt: Date.now(),
        };
        // setPins((prevPins) => [...prevPins, newPin]);
        // Remove timer after it completes
        setActiveTimers((prev) =>
          prev.filter((timer) => timer.id !== newTimer.id)
        );
      }, durationSeconds * 1000);

      setContextMenu(null);
    }else if(createPinData){

      const durationSeconds = parseInt(duration) || 15;

      // Create timer
      const newTimer = {
        id: Date.now(),
        xPercent: createPinData?.xPercent,
        yPercent: createPinData?.yPercent,
        duration: durationSeconds.toString(),
        startTime: Date.now(),
      };

      setActiveTimers((prev) => [...prev, newTimer]);
      createPin(sessionToUse,createPinData);

      // Create pin after timer completes
      setTimeout(() => {
       
        // setPins((prevPins) => [...prevPins, newPin]);
        // Remove timer after it completes
        setActiveTimers((prev) =>
          prev.filter((timer) => timer.id !== newTimer.id)
        );
      }, durationSeconds * 1000);

    }
  };



  const handleClose = () => {
    // If we're dragging in edit mode, drop the pin
    if (draggedPin && editingPinId === draggedPin) {
      setEditingPinId(null);
      setDraggedPin(null);
      setDragOffset({ x: 0, y: 0 });
      setIsDragging(false);
    }
    setContextMenu(null);
    setPinContextMenu(null);
    setPinEditDialog(null);
  };

  const handleCreateTimer = useCallback((timerData) => {
    console.log('viewedSession in handleCreateTimer',viewedSession)

    if (!timerData) return;
    
    const durationSeconds = parseInt(timerData.duration) || 15;
    
    // Add timer to active timers
    setActiveTimers((prev) => [...prev, timerData]);
    
    // Remove timer after duration completes
    setTimeout(() => {
      setActiveTimers((prev) =>
        prev.filter((timer) => timer.id !== timerData.id)
      );
      getPinsBySessionId(...viewedSession);
    }, durationSeconds * 1000);
  }, [viewedSession, getPinsBySessionId]);

  // Zoom handlers
  const handleZoomIn = useCallback(() => {
    setZoom((prev) => Math.min(prev + 25, 400));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom((prev) => Math.max(prev - 25, 25));
  }, []);

  const handleZoomToFit = useCallback(() => {
    if (imageContainerRef.current) {
      // Get the scrollable container (parent of imageContainerRef)
      const scrollableContainer = imageContainerRef.current.parentElement;
      if (!scrollableContainer) return;
      
      // Get available viewport dimensions (accounting for header)
      const containerWidth = scrollableContainer.clientWidth || window.innerWidth;
      const containerHeight = (scrollableContainer.clientHeight || window.innerHeight) - 48; // Subtract header height
      
      // Use image container base dimensions (before zoom/transform)
      // The image container has fixed dimensions: 1000x800 when not fullscreen, or viewport when fullscreen
      const baseImageWidth = isFullscreen ? containerWidth : 1000;
      const baseImageHeight = isFullscreen ? containerHeight : 800;

      if (baseImageWidth > 0 && baseImageHeight > 0 && containerWidth > 0 && containerHeight > 0) {
        const widthRatio = containerWidth / baseImageWidth;
        const heightRatio = containerHeight / baseImageHeight;

        const fitZoom = Math.floor(Math.min(widthRatio, heightRatio) * 100);
        setZoom(Math.max(25, Math.min(400, fitZoom)));
        setPanOffset({ x: 0, y: 0 });
      }
    }
  }, [isFullscreen]);

  const handleActualSize = useCallback(() => {
    setZoom(100);
    setPanOffset({ x: 0, y: 0 });
    // Don't reset rotation for actual size, only zoom and pan
  }, []);

  const handleTogglePanMode = useCallback(() => {
    setIsPanMode((prev) => !prev);
  }, []);

  const handleResetView = useCallback(() => {
    setZoom(100);
    setPanOffset({ x: 0, y: 0 });
    setIsPanMode(false);
    setRotation(0);
    // Clear persisted state when resetting
    try {
      localStorage.removeItem(TAB_STATE_KEY);
    } catch (error) {
      console.warn('Failed to clear persisted state:', error);
    }
  }, []);

  const handleFitToScreen = useCallback(() => {
    if (imageContainerRef.current) {
      // Get the scrollable container (parent of imageContainerRef)
      const scrollableContainer = imageContainerRef.current.parentElement;
      if (!scrollableContainer) return;
      
      // Get available viewport dimensions (accounting for header)
      const containerWidth = scrollableContainer.clientWidth || window.innerWidth;
      const containerHeight = (scrollableContainer.clientHeight || window.innerHeight) - 48; // Subtract header height
      
      // Use image container base dimensions (before zoom/transform)
      const baseImageWidth = isFullscreen ? containerWidth : 1000;
      const baseImageHeight = isFullscreen ? containerHeight : 800;

      if (baseImageWidth > 0 && baseImageHeight > 0 && containerWidth > 0 && containerHeight > 0) {
        // Calculate zoom to fill screen (different from fit which maintains aspect ratio)
        const widthRatio = containerWidth / baseImageWidth;
        const heightRatio = containerHeight / baseImageHeight;

        // Use the larger ratio to fill the screen
        const fillZoom = Math.floor(Math.max(widthRatio, heightRatio) * 100);
        setZoom(Math.max(25, Math.min(400, fillZoom)));
        setPanOffset({ x: 0, y: 0 });
      }
    }
  }, [isFullscreen]);

  const handleRotateImage = useCallback(() => {
    setRotation((prev) => (prev + 90) % 360);
    setMenuAnchor(null);
  }, []);

  const handlePrint = useCallback(() => {
    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Print Image</title>
            <style>
              body { margin: 0; padding: 20px; text-align: center; }
              img { max-width: 100%; height: auto; }
            </style>
          </head>
          <body>
            <img src="${imageUrl}" alt="Image" />
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    }
    setMenuAnchor(null);
  }, [imageUrl]);

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  // Extract filename from imageUrl
  const getFilename = useCallback(() => {
    if (!imageUrl) return "";
    try {
      const url = new URL(imageUrl);
      const pathname = url.pathname;
      const filename = pathname.split("/").pop();
      return filename || "Image";
    } catch {
      // If it's not a valid URL, try to extract from string
      const parts = imageUrl.split("/");
      return parts[parts.length - 1] || "Image";
    }
  }, [imageUrl]);

  // Pan handlers
  const handlePanMouseDown = (e) => {
    if (!isPanMode || draggedPin || editingPinId) return;
    e.preventDefault();
    e.stopPropagation();
    setIsPanning(true);
    setPanStart({ x: e.clientX - panOffset.x, y: e.clientY - panOffset.y });
  };

  const handlePanMouseMove = (e) => {
    if (!isPanning || !isPanMode) return;
    setPanOffset({
      x: e.clientX - panStart.x,
      y: e.clientY - panStart.y,
    });
  };

  const handlePanMouseUp = () => {
    setIsPanning(false);
  };

  const handleDeletePin = (pinId, deleteRecording = false) => {
    const pinToDelete = pins.find((pin) => pin.id === pinId) || pinContextMenu?.pin;
    if (pinToDelete) {
      // Delete the pin from local state
      setPins((prevPins) =>
        prevPins.filter((pin) => pin.id !== pinId)
      );
      // Call the deletePin function with the deleteRecording flag
      if (deletePin) {
        deletePin(pinId, deleteRecording,selectedPinId);
      }
      setPinContextMenu(null);
    }
  };

  const handleDeletePinOnly = () => {
    if (pinContextMenu && pinContextMenu.pin) {
      // Delete only the pin, keep the recording
      setPins((prevPins) =>
        prevPins.filter((pin) => pin.id !== pinContextMenu.pin.id)
      );
      deletePin(pinContextMenu.pin.id);
      setPinContextMenu(null);
    }
  };

  const handleDeleteBoth = () => {
    if (pinContextMenu && pinContextMenu.pin) {
      const pinToDelete = pinContextMenu.pin;
      // Delete the pin
      setPins((prevPins) =>
        prevPins.filter((pin) => pin.id !== pinToDelete.id)
      );
      deletePin(pinContextMenu.pin.id, true);
      // Delete associated recordings (if any)
      // For now, we'll just delete the pin since recordings are not stored separately
      // In the future, you can add logic here to delete associated recordings
      setPinContextMenu(null);
    }
  };

  const handleEditPin = (pinId) => {
    const pin = pins.find((p) => p.id === pinId) || pinContextMenu?.pin;
    if (!pin) return;

    const container = imageContainerRef.current;
    if (!container) return;

    // Get image coordinates for context menu position
    const mouseX = pinContextMenu?.mouseX || 0;
    const mouseY = pinContextMenu?.mouseY || 0;
    const imageCoords = getImageCoordinates(mouseX, mouseY);
    const { width: imageWidth, height: imageHeight } = getImageDimensions();

    // Convert pin percentage to image coordinates
    const pinX =
      pin.xPercent !== undefined ? pin.xPercent * imageWidth : pin.x || 0;
    const pinY =
      pin.yPercent !== undefined ? pin.yPercent * imageHeight : pin.y || 0;

    // Calculate offset from mouse to pin center in image coordinates
    const imageOffsetX = imageCoords.x - pinX;
    const imageOffsetY = imageCoords.y - pinY;
    // Convert to container coordinates (accounting for zoom)
    setDragOffset({
      x: imageOffsetX * (zoom / 100),
      y: imageOffsetY * (zoom / 100),
    });

    setMouseDownPos({ x: mouseX, y: mouseY });
    setDraggedPin(pin.id);
   
    setEditingPinId(pin.id);
    setIsDragging(true);
    setPinContextMenu(null);
    //updatePin(pin.id,{})
  };

  const handleClosePinMenu = () => {
    setPinContextMenu(null);
    setEditingPinId(null);
  };

  const handleClosePinEditDialog = () => {
    setPinEditDialog(null);
    setEditingPinId(null);
  };

  const handleEditLocationId = async (pinId, newLocationId) => {
    try {
      const pin = pinContextMenu?.pin || pins.find((p) => p.id === pinId);
      if (!pin || !pinId) {
        showError("Pin not found");
        return;
      }

      // Call API endpoint to update location ID
      const response = await post("/pins/updateLocationId", {
        pin_id: pinId,
        location_id: newLocationId,
      });

      if (response?.success || response) {
        // Refresh pins to show updated location_id
        if (getPinsBySessionId && viewedSession) {
          await getPinsBySessionId(...(Array.isArray(viewedSession) ? viewedSession : [viewedSession]));
        }
        showSuccess(`Location ID updated to "${newLocationId}"`);
      } else {
        showError("Failed to update location ID");
      }
    } catch (error) {
      console.error("Error updating location ID:", error);
      showError("Failed to update location ID");
    }
  };

  const handleEditPinPosition = (pinId) => {
    handleEditPin(pinId);
  };

  const handleCopyLocationId = (locationId) => {
    if (locationId) {
      showSuccess(`Location ID "${locationId}" copied to clipboard`);
    }
  };

  // Helper function to extract and format recordings from pins
  const getRecordingsFromPins = useCallback(() => {
    const recordingsMap = new Map();
    pins.forEach((pin) => {
      if (pin.construction_recordings && Array.isArray(pin.construction_recordings)) {
        pin.construction_recordings.forEach((recording) => {
          // Use filename as id if no id exists
          const recordingId = recording.id || recording.filename || `rec-${Date.now()}-${Math.random()}`;
          if (!recordingsMap.has(recordingId)) {
            recordingsMap.set(recordingId, {
              id: recordingId,
              name: recording.filename || 'Recording',
              filename: recording.filename,
              timestamp: recording.timestamp || recording.createdAt || pin.createdAt || Date.now(),
              duration: recording.duration,
            });
          }
        });
      }
    });
    return Array.from(recordingsMap.values());
  }, [pins]);

  // Wrapper function for playMp4Recording that works with recordingId
  const handleRecordingPlay = useCallback((recordingId,pinid) => {
    setSelectedPinId(pinid);
    if (!recordingId) return;
    
    // Find the recording in pins' construction_recordings
    let recordingUrl = null;
    pins.forEach((pin) => {
      if (pin.construction_recordings && Array.isArray(pin.construction_recordings)) {
        const recording = pin.construction_recordings.find(
          (rec) => (rec.id || rec.filename) === recordingId
        );
        if (recording && recording.filename) {
          const API_BASE_URL = process.env.REACT_APP_API_URL_LOGIN_AND_LOGOUT || 'https://api.dev.nvr.digitaleye.ai/api';
          recordingUrl = `${API_BASE_URL}/${recording?.recording_path}`;
        }
      }
    });

    if (recordingUrl && playMp4Recording) {
      playMp4Recording("mp4", recordingUrl, isFullscreen);
    }
  }, [pins, playMp4Recording, isFullscreen]);

  const handlePinMouseDown = (e, pin) => {
    e.preventDefault();
    e.stopPropagation();

    // Prevent dragging if any timer is running
    if (activeTimers.length > 0) {
      return;
    }

    // Only allow dragging for the pin being edited
    if (editingPinId !== pin.id) return;

    const container = imageContainerRef.current;
    if (!container) return;

    // Get image coordinates for mouse position
    const imageCoords = getImageCoordinates(e.clientX, e.clientY);
    const { width: imageWidth, height: imageHeight } = getImageDimensions();

    // Convert pin percentage to image coordinates
    const pinX =
      pin.xPercent !== undefined ? pin.xPercent * imageWidth : pin.x || 0;
    const pinY =
      pin.yPercent !== undefined ? pin.yPercent * imageHeight : pin.y || 0;

    setMouseDownPos({ x: e.clientX, y: e.clientY });
    // Calculate offset in image coordinates, then convert to container coordinates for drag
    const imageOffsetX = imageCoords.x - pinX;
    const imageOffsetY = imageCoords.y - pinY;
    // Convert to container coordinates (accounting for zoom)
    setDragOffset({
      x: imageOffsetX * (zoom / 100),
      y: imageOffsetY * (zoom / 100),
    });
    setDraggedPin(pin.id);
    setIsDragging(false);
  };

  const handleMouseMove = (e) => {
    if (!draggedPin) return;

    const container = imageContainerRef.current;
    if (!container) return;

    // Check if mouse moved significantly (more than 5px) to distinguish drag from click
    const moveDistance = Math.sqrt(
      Math.pow(e.clientX - mouseDownPos.x, 2) +
        Math.pow(e.clientY - mouseDownPos.y, 2)
    );

    if (moveDistance > 5) {
      setIsDragging(true);
    }

    // Get image coordinates accounting for zoom and pan
    const imageCoords = getImageCoordinates(e.clientX, e.clientY);
    const { width: imageWidth, height: imageHeight } = getImageDimensions();

    // Calculate drag offset in image coordinates
    const dragOffsetImageX = dragOffset.x / (zoom / 100);
    const dragOffsetImageY = dragOffset.y / (zoom / 100);

    const newX = imageCoords.x - dragOffsetImageX;
    const newY = imageCoords.y - dragOffsetImageY;

    // Convert to percentages based on image dimensions
    const xPercent = newX / imageWidth;
    const yPercent = newY / imageHeight;

    setPins((prevPins) =>
      prevPins.map((pin) =>
        pin.id === draggedPin
          ? { ...pin, xPercent: xPercent, yPercent: yPercent }
          : pin
      )
    );
  };

  const handleMouseUp = (e, pin) => {
    updatePin(pin?.id, { data: [pin] });
    if (draggedPin) {
      // If we're in edit mode, drop the pin and exit edit mode
      if (editingPinId === draggedPin) {
        setEditingPinId(null);
        setDraggedPin(null);
        setDragOffset({ x: 0, y: 0 });
        setIsDragging(false);
        updatePin(pin?.id, { data: [pin] });
        return;
      }

      // Normal drag behavior for non-edit mode
      if (pin) {
        if (!isDragging) {
          // It was a click, not a drag - open edit dialog
          setPinEditDialog({
            mouseX: e?.clientX || mouseDownPos.x,
            mouseY: e?.clientY || mouseDownPos.y,
            pin: pin,
          });
        } else {
          // We just finished dragging
          justDraggedRef.current = true;
          setTimeout(() => {
            justDraggedRef.current = false;
          }, 100);
        }
      }
      setDraggedPin(null);
      setDragOffset({ x: 0, y: 0 });
      setIsDragging(false);
    }
  };

  const handlePinClick = (e, pin) => {
    e.stopPropagation();

    // Prevent opening pin dialog if any timer is running
    if (activeTimers.length > 0) {
      return;
    }

    // Only open dialog if we didn't just drag
    if (!justDraggedRef.current && !isDragging) {
      setPinEditDialog({
        mouseX: e.clientX,
        mouseY: e.clientY,
        pin: pin,
      });
    }
  };

  const handlePinContextMenu = (e, pin) => {
    e.preventDefault();
    e.stopPropagation();

    // Prevent context menu if any timer is running
    if (activeTimers.length > 0) {
      return;
    }

    // Open PinContextMenu for the pin
    setPinContextMenu({
      mouseX: e.clientX,
      mouseY: e.clientY,
      pin: pin,
    });
  };

  useEffect(() => {
    setPins(pins1);
  }, [pins1]);

  // Fetch activity data for a directory/file
  const fetchActivities = async (directoryId) => {
    if (!directoryId) {
      setActivities([]);
      return;
    }

    setIsLoadingActivities(true);
    try {
      const response = await get(`/directory/${directoryId}/activity`);
      
      // Handle response structure: response.data.activities
      const activitiesData = response?.data?.activities || response?.activities || response?.data || [];
      
      setActivities(Array.isArray(activitiesData) ? activitiesData : []);
    } catch (error) {
      console.error("Error fetching activities:", error);
      setActivities([]);
      // Optionally show a toast error, but don't block the UI
      showError("Failed to load activity history");
    } finally {
      setIsLoadingActivities(false);
    }
  };

  // Fetch activities when info drawer opens
  useEffect(() => {
    if (showInfo && fileData?.id) {
      fetchActivities(fileData.id);
    } else if (!showInfo) {
      // Clear activities when drawer closes
      setActivities([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInfo, fileData?.id]);

  // Persist zoom, pan, and rotation state to localStorage (only for tab persistence)
  useEffect(() => {
    const currentImageId = getCurrentImageId();
    if (!currentImageId) return; // Don't persist if no image
    
    try {
      const stateToSave = {
        imageId: currentImageId,
        zoom,
        panOffset,
        rotation,
      };
      localStorage.setItem(TAB_STATE_KEY, JSON.stringify(stateToSave));
    } catch (error) {
      console.warn('Failed to save zoom/pan state:', error);
    }
  }, [zoom, panOffset, rotation, getCurrentImageId]);

  // Handle image changes: reset to default for new image, restore for same image (tab switch)
  useEffect(() => {
    const currentImageId = getCurrentImageId();
    
    // If image changed, reset to default
    if (currentImageIdRef.current !== null && currentImageIdRef.current !== currentImageId) {
      setZoom(100);
      setPanOffset({ x: 0, y: 0 });
      setRotation(0);
      currentImageIdRef.current = currentImageId;
    } else if (currentImageIdRef.current === null || currentImageIdRef.current === currentImageId) {
      // First load or same image (tab switch) - try to restore state if same image
      const newState = loadPersistedState();
      setZoom(newState.zoom);
      setPanOffset(newState.panOffset);
      setRotation(newState.rotation);
      currentImageIdRef.current = currentImageId;
    }
  }, [imageUrl, fileData?.id, fileData?.name, getCurrentImageId, loadPersistedState]);

  // Keyboard shortcuts for zoom
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger shortcuts if user is typing in an input
      if (
        e.target.tagName === "INPUT" ||
        e.target.tagName === "TEXTAREA" ||
        e.target.isContentEditable
      ) {
        return;
      }

      // Ctrl + + or Ctrl + = for zoom in
      if ((e.ctrlKey || e.metaKey) && (e.key === "+" || e.key === "=")) {
        e.preventDefault();
        handleZoomIn();
      }
      // Ctrl + - for zoom out
      else if ((e.ctrlKey || e.metaKey) && e.key === "-") {
        e.preventDefault();
        handleZoomOut();
      }
      // Ctrl + 0 for zoom to fit
      else if ((e.ctrlKey || e.metaKey) && e.key === "0") {
        e.preventDefault();
        handleZoomToFit();
      }
      // Ctrl + 1 for actual size
      else if ((e.ctrlKey || e.metaKey) && e.key === "1") {
        e.preventDefault();
        handleActualSize();
      }
      // Space for pan mode toggle
      else if (e.key === " " && !isPanMode) {
        e.preventDefault();
        handleTogglePanMode();
      }
    };

    const handleKeyUp = (e) => {
      // Release pan mode when space is released
      if (e.key === " " && isPanMode) {
        e.preventDefault();
        setIsPanMode(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [
    isPanMode,
    handleZoomIn,
    handleZoomOut,
    handleZoomToFit,
    handleActualSize,
    handleTogglePanMode,
  ]);

  // Close pin context menu on Escape key or click outside
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        if (pinContextMenu) {
          handleClosePinMenu();
        }
        if (pinEditDialog) {
          handleClosePinEditDialog();
        }
      }
    };

    const handleClickOutside = (e) => {
      if (pinContextMenu && !e.target.closest('[data-pin-context-menu]')) {
        handleClosePinMenu();
      }
      // PinEditDialog handles its own close via onClose prop, so we don't need to handle it here
    };

    if (pinContextMenu || pinEditDialog) {
      document.addEventListener("keydown", handleKeyDown);
      if (pinContextMenu) {
        document.addEventListener("click", handleClickOutside);
      }
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("click", handleClickOutside);
    };
  }, [pinContextMenu, pinEditDialog]);

  const toggleFullscreen = () => {
    const container = containerRef.current;
    if (!container) return;

    if (!isFullscreen) {
      if (container.requestFullscreen) {
        container.requestFullscreen();
      } else if (container.webkitRequestFullscreen) {
        container.webkitRequestFullscreen();
      } else if (container.msRequestFullscreen) {
        container.msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  };

  useEffect(() => {
    const checkFullscreenSupport = () => {
      // Check fullscreen support on the outermost container
      const container = containerRef.current;
      if (container) {
        const supported =
          container.requestFullscreen ||
          container.webkitRequestFullscreen ||
          container.msRequestFullscreen ||
          false;
        setIsFullscreenSupported(!!supported);
      }
    };

    checkFullscreenSupport();

    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("msfullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "msfullscreenchange",
        handleFullscreenChange
      );
    };
  }, []);

  // Add native wheel event listener for Ctrl+wheel zoom
  useEffect(() => {
    const container = imageContainerRef.current;
    if (!container) return;

    const handleWheelNative = (e) => {
      // Only zoom if Ctrl key is pressed
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        // Determine zoom direction based on wheel delta
        const delta = e.deltaY;
        const zoomStep = 10; // Smaller step for smoother zooming
        
        if (delta < 0) {
          // Scrolling up - zoom in
          setZoom((prev) => Math.min(prev + zoomStep, 400));
        } else if (delta > 0) {
          // Scrolling down - zoom out
          setZoom((prev) => Math.max(prev - zoomStep, 25));
        }
      }
    };

    // Use native event listener with passive: false to allow preventDefault
    container.addEventListener("wheel", handleWheelNative, { passive: false });

    return () => {
      container.removeEventListener("wheel", handleWheelNative);
    };
  }, []);

  if (imageUrl === undefined || imageUrl === null || imageUrl === "") {
    return <div>No image URL provided</div>;
  }

  return (
    <div
      ref={containerRef}
      style={{
        width: isFullscreen ? "100vw" : "100%",
        height: isFullscreen ? "100vh" : "100%",
        overflow: isFullscreen ? "hidden" : "auto",
        overflowX: isFullscreen ? "hidden" : "auto",
        overflowY: isFullscreen ? "hidden" : "auto",
        position: "relative",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Scrollable Content Container */}
      <div
        style={{
          flex: "1 1 auto",
          overflow: "auto",
          position: "relative",
          minHeight: 0,
        }}
      >
        {/* Header Wrapper - Matches container width */}
        {isFullscreenSupported && (
          <FullscreenButton
            isFullscreen={isFullscreen}
            onToggle={toggleFullscreen}
          />
        )}
        <div
          style={{
            position: "sticky",
            top: 0,
            left: 0,
            width: isFullscreen ? "100vw" : "100%",
            // minWidth: isFullscreen ? "100vw" : "1000px",
            zIndex: 1000,
            backgroundColor: "rgba(60, 60, 60, 0.95)",
          }}
        >
          {/* Header Bar */}
          <Box
            sx={{
              width: "100%",
              height: "48px",
              backgroundColor: "rgba(60, 60, 60, 0.95)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "0 8px",
              borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Left: Filename */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flex: "0 0 auto",
                minWidth: 0,
                marginRight: "16px",
              }}
            >
              {/* <IconButton
            size="small"
            sx={{
              color: "rgba(255, 255, 255, 0.7)",
              padding: "4px",
              marginRight: "8px",
            }}
            onClick={(e) => {
              e.stopPropagation();
              // Close handler if needed
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton> */}
              <Typography
                variant="body2"
                sx={{
                  color: "rgba(255, 255, 255, 0.9)",
                  fontSize: "14px",
                  fontWeight: 500,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "300px",
                }}
              >
                {getFilename()}
              </Typography>
            </Box>

            {/* Middle: Control Buttons */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                flex: "1 1 auto",
                justifyContent: "center",
              }}
            >
              <Tooltip title="Zoom In" arrow>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleZoomIn();
                  }}
                  sx={{
                    color: "rgba(255, 255, 255, 0.7)",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                      color: "rgba(255, 255, 255, 1)",
                    },
                  }}
                >
                  <ZoomInIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Zoom Out" arrow>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleZoomOut();
                  }}
                  sx={{
                    color: "rgba(255, 255, 255, 0.7)",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                      color: "rgba(255, 255, 255, 1)",
                    },
                  }}
                >
                  <ZoomOutIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Box
                sx={{
                  width: "1px",
                  height: "24px",
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  margin: "0 4px",
                }}
              />

              <Tooltip title="Pan Mode" arrow>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTogglePanMode();
                  }}
                  sx={{
                    color: isPanMode
                      ? "rgba(255, 255, 255, 1)"
                      : "rgba(255, 255, 255, 0.7)",
                    backgroundColor: isPanMode
                      ? "rgba(255, 255, 255, 0.2)"
                      : "transparent",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                      color: "rgba(255, 255, 255, 1)",
                    },
                  }}
                >
                  <PanToolIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Actual Size" arrow>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleActualSize();
                  }}
                  sx={{
                    color: "rgba(255, 255, 255, 0.7)",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                      color: "rgba(255, 255, 255, 1)",
                    },
                  }}
                >
                  <AspectRatioIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Zoom to Fit" arrow>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleZoomToFit();
                  }}
                  sx={{
                    color: "rgba(255, 255, 255, 0.7)",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                      color: "rgba(255, 255, 255, 1)",
                    },
                  }}
                >
                  <FitScreenIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Reset View" arrow>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleResetView();
                  }}
                  sx={{
                    color: "rgba(255, 255, 255, 0.7)",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                      color: "rgba(255, 255, 255, 1)",
                    },
                  }}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Fit to Screen" arrow>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFitToScreen();
                  }}
                  sx={{
                    color: "rgba(255, 255, 255, 0.7)",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                      color: "rgba(255, 255, 255, 1)",
                    },
                  }}
                >
                  <CropFreeIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Right: Action Buttons */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                flex: "0 0 auto",
                marginLeft: "16px",
              }}
            >
              <Tooltip
                title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                arrow
              >
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFullscreen();
                  }}
                  sx={{
                    color: "rgba(255, 255, 255, 0.7)",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                      color: "rgba(255, 255, 255, 1)",
                    },
                  }}
                >
                  {isFullscreen ? (
                    <FullscreenExitIcon fontSize="small" />
                  ) : (
                    <FullscreenIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>

              <Box
                sx={{
                  width: "1px",
                  height: "24px",
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  margin: "0 4px",
                }}
              />

              <Tooltip title="Info" arrow>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowInfo(!showInfo);
                  }}
                  sx={{
                    color: showInfo
                      ? "rgba(255, 255, 255, 1)"
                      : "rgba(255, 255, 255, 0.9)",
                    backgroundColor: showInfo
                      ? "rgba(255, 255, 255, 0.25)"
                      : "rgba(255, 255, 255, 0.05)",
                    border: showInfo
                      ? "1px solid rgba(255, 255, 255, 0.3)"
                      : "1px solid rgba(255, 255, 255, 0.1)",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.15)",
                      color: "rgba(255, 255, 255, 1)",
                      border: "1px solid rgba(255, 255, 255, 0.3)",
                    },
                    zIndex: 10,
                  }}
                >
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Box
                sx={{
                  width: "1px",
                  height: "24px",
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  margin: "0 4px",
                }}
              />

            <Tooltip title="View more option" arrow>
            <IconButton
                size="small"
                onClick={handleMenuOpen}
                sx={{
                  color: "rgba(255, 255, 255, 0.7)",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    color: "rgba(255, 255, 255, 1)",
                  },
                }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Tooltip>

              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
                onClick={(e) => e.stopPropagation()}
                PaperProps={{
                  sx: {
                    backgroundColor: "rgba(60, 60, 60, 0.95)",
                    color: "rgba(255, 255, 255, 0.9)",
                    minWidth: "150px",
                  },
                }}
              >
                <MenuItem
                  onClick={handlePrint}
                  sx={{
                    color: "rgba(255, 255, 255, 0.9)",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                    },
                  }}
                >
                  <PrintIcon sx={{ marginRight: "8px", fontSize: "20px" }} />
                  Print
                </MenuItem>
                <MenuItem
                  onClick={handleRotateImage}
                  sx={{
                    color: "rgba(255, 255, 255, 0.9)",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                    },
                  }}
                >
                  <RotateRightIcon
                    sx={{ marginRight: "8px", fontSize: "20px" }}
                  />
                  Rotate
                </MenuItem>
              </Menu>
            </Box>
          </Box>
        </div>

        {/* Info Panel */}
        {/* {showInfo && (
          <Box
            sx={{
              position: "absolute",
              top: "48px",
              right: "8px",
              backgroundColor: "rgba(60, 60, 60, 0.95)",
              padding: "16px",
              borderRadius: "4px",
              zIndex: 999,
              minWidth: "200px",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.3)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography
              variant="body2"
              sx={{ color: "rgba(255, 255, 255, 0.9)", marginBottom: "8px" }}
            >
              <strong>Image Info</strong>
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "rgba(255, 255, 255, 0.7)", display: "block" }}
            >
              Filename: {getFilename()}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "rgba(255, 255, 255, 0.7)", display: "block" }}
            >
              Zoom: {zoom}%
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "rgba(255, 255, 255, 0.7)", display: "block" }}
            >
              Rotation: {rotation}°
            </Typography>
          </Box>
        )} */}

        {/* Info Drawer */}
        <ViewerInfoDrawer
          open={showInfo}
          onClose={() => setShowInfo(false)}
          container={isFullscreen ? containerRef.current : null}
          fileName={getFilename()}
          fileDetails={(() => {
            // Format file size
            const formatFileSize = (bytes) => {
              if (!bytes) return "N/A";
              if (bytes < 1024) return `${bytes} B`;
              if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
              return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
            };

            // Format date
            const formatDate = (dateString) => {
              if (!dateString) return "N/A";
              try {
                const date = new Date(dateString);
                return date.toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                });
              } catch (e) {
                return "N/A";
              }
            };

            // Get file type from mime type or file extension
            const getFileType = () => {
              if (fileData?.mime_type || fileData?.mimeType) {
                const mimeType = fileData.mime_type || fileData.mimeType;
                if (mimeType.includes("pdf")) return "PDF Document";
                if (mimeType.includes("image")) return "Image Document";
                return mimeType.split("/")[1]?.toUpperCase() || "Document";
              }
              if (fileData?.file_type || fileData?.fileType) {
                const fileType = (
                  fileData.file_type || fileData.fileType
                ).toUpperCase();
                return fileType === "PDF"
                  ? "PDF Document"
                  : `${fileType} Document`;
              }
              // Try to infer from filename
              const filename = getFilename().toLowerCase();
              if (
                filename.endsWith(".jpg") ||
                filename.endsWith(".jpeg") ||
                filename.endsWith(".png") ||
                filename.endsWith(".gif")
              ) {
                return "Image Document";
              }
              return "Image Document";
            };

            // Get location/path
            const getLocation = () => {
              if (fileData?.directory_path) return fileData.directory_path;
              if (fileData?.path) return fileData.path;
              if (fileData?.parent_id) return "My Documents";
              return "My Documents";
            };

            return {
              owner: fileData?.owner || "Unknown",
              dateModified: formatDate(
                fileData?.updated_at || fileData?.dateModified
              ),
              dateCreated: formatDate(
                fileData?.created_at || fileData?.dateCreated
              ),
              fileType: getFileType(),
              size: formatFileSize(fileData?.size),
              description: fileData?.description || "",
              location: getLocation(),
            };
          })()}
          activities={activities}
          isLoadingActivities={isLoadingActivities}
          comments={[]}
        />

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            minWidth: isFullscreen ? "auto" : "100%",
            minHeight: isFullscreen ? "auto" : "calc(100% - 48px)",
            width: isFullscreen ? "100%" : "max-content",
            height: isFullscreen ? "calc(100% - 48px)" : "max-content",
            paddingTop: "0",
          }}
        >
          <div
            ref={imageContainerRef}
            className="drag_drop_container"
            style={{
              position: "relative",
              display: "inline-block",
              width: isFullscreen ? "100vw" : "1000px",
              height: isFullscreen ? "calc(100vh - 48px)" : "800px",
              minWidth: isFullscreen ? "auto" : "1000px",
              minHeight: isFullscreen ? "auto" : "800px",
              maxWidth: isFullscreen ? "100%" : "none",
              maxHeight: isFullscreen ? "100%" : "none",
              objectFit: "contain",
              cursor: isPanMode
                ? isPanning
                  ? "grabbing"
                  : "grab"
                : draggedPin
                ? "grabbing"
                : "default",
              overflow: isFullscreen ? "hidden" : "visible",
              flexShrink: 0,
              marginTop: "0",
            }}
            onContextMenu={handleContextMenu}
            onClick={(e) => {
              // Don't close context menus if drawer is open
              if (!showInfo) {
                handleClose(e);
              }
            }}
            onMouseMove={(e) => {
              handleMouseMove(e);
              handlePanMouseMove(e);
            }}
            onMouseDown={(e) => {
              if (isPanMode && !draggedPin && !editingPinId) {
                handlePanMouseDown(e);
              }
            }}
            onMouseUp={(e) => {
              if (draggedPin) {
                const pin = pins.find((p) => p.id === draggedPin);
                handleMouseUp(e, pin);
              }
              handlePanMouseUp();
            }}
            onMouseLeave={(e) => {
              if (draggedPin) {
                const pin = pins.find((p) => p.id === draggedPin);
                handleMouseUp(e, pin);
              }
              handlePanMouseUp();
            }}
          >
            <div
              style={{
                transform: `scale(${zoom / 100}) translate(${
                  panOffset.x / (zoom / 100)
                }px, ${panOffset.y / (zoom / 100)}px) rotate(${rotation}deg)`,
                transformOrigin: "center center",
                transition: isPanning ? "none" : "transform 0.2s ease",
                width: isFullscreen ? "100vw" : "1000px",
                height: isFullscreen ? "calc(100vh - 48px)" : "800px",
                position: "relative",
              }}
            >
              <img
                style={{ width: "100%", height: "100%", display: "block" }}
               
                src={imageUrl}
                alt={imageUrl ? "Construction site image" : "Image not available"}
                draggable={false}
              />

              {/* Overlay when recording */}
              {activeTimers.length > 0 && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    backgroundColor: "rgba(0, 0, 0, 0.2)",
                    zIndex: 150,
                    pointerEvents: "auto",
                    cursor: "not-allowed",
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                />
              )}

              {/* Render active timers */}
              {activeTimers.map((timer) => (
                <Timer key={timer.id} timer={timer} />
              ))}

              {/* Render pins */}
              {pins.map((pin) => {
                const { width: imageWidth, height: imageHeight } =
                  getImageDimensions();
                return (
                  <Pin
                  key={pin.id}
                  pin={pin}
                  containerRef={imageContainerRef}
                  isInEditMode={editingPinId === pin.id}
                  isDragged={draggedPin === pin.id}
                  onMouseDown={handlePinMouseDown}
                  onClick={handlePinClick}
                  onContextMenu={handlePinContextMenu}
                  isViewingPreviousSession={isViewingPreviousSession}
                  currentSession={currentSession}
                  viewedSession={viewedSession}
                  activeTimers={activeTimers}
                  selectedDate={selectedDate}
                  imageWidth={imageWidth}
                  imageHeight={imageHeight}
                  showLabel={showLabel}
                  selectedDrawingForView={selectedDrawingForView}

                  />
                );
              })}
            </div>

            <ImageContextMenu
              contextMenu={contextMenu}
              onAddPin={handleAddPin}
              pins={pins}
              zoom={zoom}
              onZoomIn={handleZoomIn}
              onZoomOut={handleZoomOut}
              onZoomToFit={handleZoomToFit}
              onActualSize={handleActualSize}
              isPanMode={isPanMode}
              onTogglePanMode={handleTogglePanMode}
              onResetView={handleResetView}
            />

            <PinContextMenu
              pinContextMenu={pinContextMenu}
              onDeletePin={handleDeletePin}
              onDeletePinOnly={handleDeletePinOnly}
              onDeleteBoth={handleDeleteBoth}
              onEditLocationId={handleEditLocationId}
              onEditPinPosition={handleEditPinPosition}
              onCopyLocationId={handleCopyLocationId}
              onClose={handleClosePinMenu}
              selectedDrawingForView={selectedDrawingForView}
            />


            <PinEditDialog
              open={pinEditDialog !== null}
              pinContextMenu={pinEditDialog}
              onClose={handleClosePinEditDialog}
              pin={(() => {
                // Map pin to include all fields expected by PinEditDialog
                const pin = pinEditDialog?.pin;
                if (!pin) return null;
             
                
                const firstRecording = pin.construction_recordings?.[0];
                const recordingId = firstRecording ? (firstRecording.id || firstRecording.filename) : pin.recordingId;
                
                // Convert timestamp to proper format
                let timestamp = pin.timestamp || pin.createdAt || Date.now();
                // If timestamp is a number, ensure it's treated as milliseconds
                if (typeof timestamp === 'number' && timestamp < 10000000000) {
                  // If it's in seconds, convert to milliseconds
                  timestamp = timestamp * 1000;
                }
                // If it's a string, try to parse it
                if (typeof timestamp === 'string') {
                  const parsed = new Date(timestamp);
                  timestamp = isNaN(parsed.getTime()) ? Date.now() : parsed.getTime();
                }
                
                // Map pin data to match PinEditDialog expectations
                return {
                  ...pin,
                  // Ensure recordingId is set
                  recordingId: recordingId,
                  // Map timestamp (ensure it's a valid date value)
                  timestamp: timestamp,
                  // Map sessionNumber from session_id or viewedSession
                  sessionNumber: pin.sessionNumber || pin.session_id || viewedSession?.id || viewedSession || 1,
                  // Ensure pageNumber exists (default to 1 for images)
                  pageNumber: pin.pageNumber || pin.page_number || 1,
                  // Ensure drawingPath exists
                  drawingPath: pin.drawingPath || pin.drawing_path || fileData?.name || fileData?.file_path || 'Unknown',
                  // Ensure label exists
                  label: pin.label || pin.name || '',
                  // Ensure type exists
                  type: pin.type || (recordingId ? 'recording' : 'manual'),
                  // Ensure lNumber exists (extract from pin if available)
                  lNumber: pin.lNumber || pin.l_number || undefined,
                  // Ensure pin field exists for number extraction
                  pin: pin.pin || pin.label || `Pin ${pin.id}`,
                  sessionId:pin.construction_recordings?.[0]?.session_id
                };
              })()}
              onDelete={handleDeletePin}
              onEditPosition={handleEditPinPosition}
              onRecordingClick={handleRecordingPlay}
              onRecordingPlay={handleRecordingPlay}
              recordings={getRecordingsFromPins()}
              currentSession={viewedSession?.name?.split('_')[1] || viewedSession}
              containerRef={imageContainerRef}  
              isFullscreen={isFullscreen}
              handleAddPin={handleAddPin}
              onCreateTimer={handleCreateTimer}
              currentSessionId={currentSession?.id}
              selectedDrawingForView={selectedDrawingForView}
            />  
            {((isFullscreenSupported && isFullscreen) && recordingsTabFullscreenOpen) ? (
              <FullscreenRecodingTab
                renderCamera360={renderCamera360}
                isFullscreen={isFullscreen}
                recordingsTabFullscreenOpen={recordingsTabFullscreenOpen}
                onCloseFullscreen={onCloseFullscreen}
                streamType={streamType}
                mp4Url={mp4Url}
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PinImageContainer;
